import { FormEvent, useEffect, useRef, useState } from "react";
import {
  FinancialConfirmationCancelled,
  suggestedPaymentReason,
  type FinancialConfirmation,
} from "../api/financial-authorization";
import {
  requestFinancialChallenge,
  verifyFinancialChallenge,
} from "../api/fiscal-confirmation";
import { ShieldCheck } from "lucide-react";
import { Button, SelectField, TextField } from "./charte";

/**
 * Motif proposé pour un encaissement saisi à la main (20/10/2026) : la pièce réglée et l'élève, toujours
 * modifiable. Les autres opérations gardent un motif à écrire (ou celui déjà donné par l'écran d'origine).
 */
function suggestedReason(detail: FinancialConfirmation) {
  if (detail.method !== "POST" || detail.path !== "/v1/payments") return "";
  return suggestedPaymentReason(detail.payload);
}

const euros = (amount: number) =>
  amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/**
 * Confirmation de sécurité des opérations sensibles (encaissements, annulations, remboursements, avoirs,
 * catalogue, accès de l'équipe, exports…). Fenêtre de la charte depuis le 20/10/2026 : même déroulé, mêmes
 * libellés et mêmes appels qu'avant.
 */
export function FinancialConfirmationDialog() {
  const pending = useRef<FinancialConfirmation | null>(null);
  const panel = useRef<HTMLFormElement>(null);
  const [action, setAction] = useState<FinancialConfirmation | null>(null);
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState<"password" | "email">("password");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<FinancialConfirmation>).detail;
      if (pending.current) {
        detail.reject(
          new FinancialConfirmationCancelled(
            "Une confirmation est déjà ouverte.",
          ),
        );
        return;
      }
      pending.current = detail;
      setAction(detail);
      setChallengeId(null);
      setSecret("");
      setError("");
      setBusy(false);
      setMethod("password");
      setReason(
        detail.method === "POST" && detail.path === "/v1/auth/invite"
          ? "Invitation d’un membre de l’équipe"
          : typeof detail.payload.reason === "string"
            ? detail.payload.reason.trim()
            : suggestedReason(detail),
      );
    };
    window.addEventListener("permio:financial-confirmation", receive);
    return () => {
      window.removeEventListener("permio:financial-confirmation", receive);
      pending.current?.reject(
        new FinancialConfirmationCancelled("Confirmation interrompue."),
      );
      pending.current = null;
    };
  }, []);
  useEffect(() => {
    if (!action) return;
    const previous = document.activeElement as HTMLElement | null;
    // Motif déjà rempli : le curseur va directement au mot de passe.
    (
      panel.current?.querySelector<HTMLInputElement>("[data-autofocus]") ??
      panel.current?.querySelector<HTMLInputElement>("input")
    )?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [action]);
  if (!action) return null;
  const close = () => {
    pending.current = null;
    setAction(null);
    setSecret("");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      let id = challengeId;
      if (!id) {
        const data = await requestFinancialChallenge(
          { method: action.method, path: action.path, payload: action.payload },
          method,
          reason.trim(),
        );
        id = data.challengeId;
        setChallengeId(id);
        if (method === "email") {
          setBusy(false);
          return;
        }
      }
      action.resolve(await verifyFinancialChallenge(id, secret));
      close();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      setError(
        typeof message === "string"
          ? message
          : "Confirmation impossible. Fermez puis réessayez si elle a expiré.",
      );
      setSecret("");
    } finally {
      setBusy(false);
    }
  };
  const invitation =
    action.method === "POST" && action.path === "/v1/auth/invite";
  const label = invitation
    ? "Invitation d’un membre de l’équipe"
    : action.path.endsWith("/cancel")
      ? "Annulation"
      : action.path.endsWith("/correct")
        ? "Correction"
        : action.path.endsWith("/refund")
          ? "Remboursement"
          : action.path === "/v1/payments/export"
            ? "Export des encaissements"
            : action.path.startsWith("/v1/payments")
              ? "Validation de l’encaissement"
              : "Confirmation de sécurité";
  const cancel = () => {
    action.reject(
      new FinancialConfirmationCancelled("Opération non confirmée."),
    );
    close();
  };
  const reasonGiven = invitation || reason.trim().length >= 3;
  // Au-dessus de toute autre fenêtre, y compris celles de l'ancien style (`.modal`, z-index 2000) qui l'ouvrent.
  return (
    <div className="charte-root v2-root fixed inset-0 z-[2100] grid place-items-center p-4 font-sans tracking-[-.2px]">
      <div
        className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-[2px] animate-v2-fade-in"
        aria-hidden="true"
      />
      <form
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="financial-confirmation-title"
        aria-describedby="financial-confirmation-description"
        aria-busy={busy}
        className="relative flex max-h-[calc(100vh-32px)] w-full max-w-[440px] flex-col gap-4 overflow-y-auto rounded-card bg-white p-6 shadow-[rgba(0,0,0,.08)_0_24px_60px_-12px,rgba(0,0,0,.05)_0_2px_8px_-2px] animate-v2-fade-in max-sm:p-5"
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) {
            event.preventDefault();
            cancel();
          }
          if (event.key !== "Tab") return;
          const controls = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              "input:not(:disabled), select:not(:disabled), button:not(:disabled)",
            ),
          );
          const first = controls[0],
            last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <header className="flex items-center gap-3">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary-50 text-primary"
            aria-hidden="true"
          >
            <ShieldCheck size={21} strokeWidth={1.9} />
          </span>
          <div className="min-w-0">
            <p className="text-[12px] font-medium text-secondary">
              Sécurité de votre espace
            </p>
            <h2
              id="financial-confirmation-title"
              className="text-title-sm text-heading"
            >
              Confirmer l’opération
            </h2>
          </div>
        </header>
        <p className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-[14px] bg-surface-indigo px-4 py-3 text-[13.5px] font-medium text-primary-900">
          <span>{label}</span>
          {typeof action.payload.amount === "number" && (
            <>
              <span className="sr-only"> — </span>
              <span className="font-semibold tabular-nums">
                {euros(action.payload.amount)}
              </span>
            </>
          )}
        </p>
        <p
          id="financial-confirmation-description"
          className="-mt-1 text-[13px] leading-relaxed text-secondary"
        >
          {invitation
            ? "Confirmez votre identité pour autoriser cette invitation. Aucun encaissement n’est effectué."
            : "Cette confirmation est valable pour cette opération uniquement."}
        </p>
        {!invitation && (
          <TextField
            label="Motif"
            disabled={busy}
            required
            minLength={3}
            maxLength={1000}
            placeholder="Précisez le motif de cette opération"
            value={reason}
            readOnly={
              typeof action.payload.reason === "string" || !!challengeId
            }
            className="read-only:cursor-default read-only:bg-surface-2 read-only:text-secondary"
            onChange={(e) => setReason(e.target.value)}
          />
        )}
        {!challengeId && (
          <SelectField
            label="Méthode de confirmation"
            value={method}
            disabled={busy}
            onChange={(e) => {
              setMethod(e.target.value as "password" | "email");
              setSecret("");
            }}
          >
            <option value="password">Mot de passe Permio</option>
            <option value="email">Code par e-mail (également pour OAuth)</option>
          </SelectField>
        )}
        {(method === "password" || challengeId) && (
          <TextField
            label={method === "password" ? "Mot de passe" : "Code reçu par e-mail"}
            disabled={busy}
            type={method === "password" ? "password" : "text"}
            autoComplete={
              method === "password" ? "current-password" : "one-time-code"
            }
            required
            maxLength={1024}
            value={secret}
            data-autofocus={reasonGiven || undefined}
            onChange={(e) => setSecret(e.target.value)}
          />
        )}
        {method === "email" && (
          <p className="text-[12.5px] leading-relaxed text-secondary">
            Le code est envoyé uniquement à l’adresse enregistrée de votre
            compte. Il expire après cinq minutes.
          </p>
        )}
        {error && (
          <p
            role="alert"
            className="rounded-xl bg-danger-light px-3.5 py-2.5 text-[13px] leading-snug text-danger-dark"
          >
            {error}
          </p>
        )}
        <div className="mt-1 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" disabled={busy} onClick={cancel}>
            Abandonner
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy
              ? "Vérification…"
              : method === "email" && !challengeId
                ? "Envoyer le code"
                : "Confirmer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
